import { useState, useEffect } from "react";
import { useAuth } from "@/providers/AuthProvider";
import { useParams, useSearchParams, useNavigate } from "react-router";
import { ErrorDisplay } from "./ui/error";
import { Button } from "./ui/button";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "./ui/tabs";
import { Loader } from "./ui/loader";
import { Assessment, Candidate, AssessmentQuestion } from "@/types";
import { apiRequest } from "@/utils/api";
import { API_ENDPOINTS } from "@/config/api";
import { PageHeading } from "@/components/ui/page-heading";
import { PageContent } from "@/components/ui/page-content";
import { SubHeading } from "./ui/sub-heading";
import {
  PauseIcon,
  PlayIcon,
  LockClosedIcon,
  TrashIcon,
} from "@radix-ui/react-icons";
import { Tooltip } from "./ui/tooltip";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
  DialogDescription,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { InviteDialog } from "./InviteDialog";
import { StatusBadge } from "./ui/status-badge";
import { useToast } from "./ui/use-toast";
import { ShareDialog } from "./ShareDialog";
import { EditableAssessmentTitle } from "./ui/editable-assessment-title";
import { Checkbox } from "./ui/checkbox";

function EditQuestionsDialog({
  assessmentId,
  questions,
  onQuestionsUpdated,
  disabled = false,
}: {
  assessmentId: string;
  questions: AssessmentQuestion[];
  onQuestionsUpdated: () => void;
  disabled?: boolean;
}) {
  const [open, setOpen] = useState(false);
  const [editedQuestions, setEditedQuestions] = useState<AssessmentQuestion[]>(
    []
  );
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState("");
  const { toast } = useToast();

  useEffect(() => {
    setEditedQuestions(questions);
  }, [questions]);

  const handleQuestionChange = (
    id: string,
    field: keyof AssessmentQuestion,
    value: string
  ) => {
    setEditedQuestions((prev) =>
      prev.map((q) =>
        q.id === id
          ? {
              ...q,
              [field]: value,
            }
          : q
      )
    );
  };

  const handleSubmit = async () => {
    setError("");
    setIsLoading(true);

    try {
      await apiRequest(`${API_ENDPOINTS.assessments.list}/${assessmentId}`, {
        method: "PATCH",
        body: { questions: editedQuestions },
      });

      onQuestionsUpdated();
      setOpen(false);
      toast({
        title: "Questions updated",
        description: "Assessment questions updated successfully",
        variant: "default",
      });
    } catch (err: any) {
      setError(err.message);
      toast({
        title: "Error",
        description: err.message,
        variant: "destructive",
      });
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        {disabled ? (
          <Tooltip content="Questions cannot be edited after publishing or pausing the assessment">
            <div>
              <Button
                variant="secondary"
                disabled={true}
                icon={<LockClosedIcon className="h-4 w-4" />}
              >
                Questions locked
              </Button>
            </div>
          </Tooltip>
        ) : (
          <Button variant="secondary">Edit questions</Button>
        )}
      </DialogTrigger>
      <DialogContent className="max-w-3xl">
        <DialogHeader>
          <DialogTitle>Edit assessment questions</DialogTitle>
        </DialogHeader>
        <div className="space-y-6 py-4 max-h-[60vh] overflow-y-auto">
          {editedQuestions.map((question, index) => (
            <div key={question.id} className="space-y-4 p-4 border rounded-lg">
              <div className="flex items-center gap-2">
                <span className="text-sm text-muted-foreground">
                  #{index + 1}
                </span>
                <Input
                  value={question.text}
                  onChange={(e) =>
                    handleQuestionChange(question.id, "text", e.target.value)
                  }
                  placeholder="Enter question text"
                />
              </div>
              {question.type === "multiple-choice" && question.options && (
                <div className="space-y-2 pl-6">
                  {question.options.map((option, optionIndex) => (
                    <div key={optionIndex} className="flex items-center gap-2">
                      <span className="w-6 h-6 flex items-center justify-center border rounded-full text-xs">
                        {String.fromCharCode(65 + optionIndex)}
                      </span>
                      <Input
                        value={option}
                        onChange={(e) => {
                          const newOptions = [...question.options!];
                          newOptions[optionIndex] = e.target.value;
                          handleQuestionChange(
                            question.id,
                            "options",
                            JSON.stringify(newOptions)
                          );
                        }}
                        placeholder={`Option ${optionIndex + 1}`}
                      />
                    </div>
                  ))}
                </div>
              )}
            </div>
          ))}
          {error && <ErrorDisplay message={error} className="mt-4" />}
        </div>
        <DialogFooter>
          <Button type="submit" onClick={handleSubmit} isLoading={isLoading}>
            Save changes
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export function AssessmentView() {
  const { uuid } = useParams();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { activeTeam } = useAuth();
  const [assessment, setAssessment] = useState<Assessment | null>(null);
  const [candidates, setCandidates] = useState<Candidate[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isCandidatesLoading, setIsCandidatesLoading] = useState(true);
  const [isPublishing, setIsPublishing] = useState(false);
  const [isPausing, setIsPausing] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [selectedCandidates, setSelectedCandidates] = useState<string[]>([]);
  const [showRemoveCandidatesDialog, setShowRemoveCandidatesDialog] =
    useState(false);
  const [error, setError] = useState("");

  const { toast } = useToast();

  // Function to publish the assessment
  const handlePublishAssessment = async () => {
    if (!assessment?.id || !activeTeam) return;

    setIsPublishing(true);
    setError("");
    try {
      const updatedAssessment = await apiRequest<Assessment>(
        `${API_ENDPOINTS.assessments.list}/${assessment.id}`,
        {
          method: "PATCH",
          body: { status: "published", team_id: activeTeam.id },
        }
      );

      setAssessment(updatedAssessment);
      toast({
        title: "Assessment published",
        description: "You can now invite candidates to take this assessment.",
        variant: "default",
      });
    } catch (err: any) {
      setError(err.message);
    } finally {
      setIsPublishing(false);
    }
  };

  // Function to pause/unpause the assessment
  const handlePauseToggle = async () => {
    if (!assessment?.id || !activeTeam) return;

    const newStatus = assessment.status === "paused" ? "published" : "paused";
    const actionText = assessment.status === "paused" ? "unpaused" : "paused";

    setIsPausing(true);
    setError("");
    try {
      const updatedAssessment = await apiRequest<Assessment>(
        `${API_ENDPOINTS.assessments.list}/${assessment.id}`,
        {
          method: "PATCH",
          body: { status: newStatus, team_id: activeTeam.id },
        }
      );

      setAssessment(updatedAssessment);
      toast({
        title: `Assessment ${actionText}`,
        description:
          assessment.status === "paused"
            ? "Candidates can now access this assessment again."
            : "Candidates will not be able to access this assessment until it is unpaused.",
        variant: "default",
      });
    } catch (err: any) {
      setError(err.message);
    } finally {
      setIsPausing(false);
    }
  };

  // Get the active tab from URL query param or default to "questions"
  const activeTab = searchParams.get("tab") || "questions";

  // Function to update the URL when tab changes
  const handleTabChange = (value: string) => {
    // If trying to access candidates tab but assessment is not published, redirect to questions tab
    if (value === "candidates" && assessment?.status !== "published") {
      value = "questions";
      toast({
        title: "Assessment not published",
        description:
          "You need to publish the assessment before inviting candidates.",
        variant: "default",
      });
    }

    navigate(`/assessments/${uuid}?tab=${value}`, { replace: true });

    // If changing to candidates tab, reload the candidates data
    if (value === "candidates") {
      loadCandidates();
    }
  };

  // Function to load candidates data
  const loadCandidates = async () => {
    if (!uuid || !activeTeam) return;

    setIsCandidatesLoading(true);
    try {
      const candidatesData = await apiRequest<Candidate[]>(
        `${API_ENDPOINTS.assessments.candidates(uuid)}?team_id=${activeTeam.id}`
      );
      setCandidates(candidatesData);
    } catch (err) {
      console.error("Failed to load candidates:", err);
      toast({
        title: "Error",
        description: "Failed to load candidates",
        variant: "destructive",
      });
    } finally {
      setIsCandidatesLoading(false);
    }
  };

  useEffect(() => {
    const fetchData = async () => {
      if (!uuid || !activeTeam) return;

      try {
        const [assessmentData, candidatesData] = await Promise.all([
          apiRequest<Assessment>(`${API_ENDPOINTS.assessments.list}/${uuid}?team_id=${activeTeam.id}`),
          apiRequest<Candidate[]>(`${API_ENDPOINTS.assessments.candidates(uuid)}?team_id=${activeTeam.id}`),
        ]);
        setAssessment(assessmentData);
        setCandidates(candidatesData);
      } catch (err: any) {
        setError(
          err instanceof Error ? err.message : "Failed to load assessment"
        );
        toast({
          title: "Error",
          description: err.message,
          variant: "destructive",
        });
      } finally {
        setIsLoading(false);
        setIsCandidatesLoading(false);
      }
    };

    if (activeTeam) {
      fetchData();
    }
  }, [uuid, toast, activeTeam]);

  if (isLoading)
    return (
      <PageContent>
        <Loader title="Loading assessment" />
      </PageContent>
    );
  if (!assessment || !assessment?.id) return <div>Assessment not found</div>;

  const questions = assessment.questions || [];

  return (
    <>
      <PageHeading>
        <div className="flex items-center justify-between w-full">
          <div>
            <div className="flex items-center gap-3">
              <EditableAssessmentTitle
                assessment={assessment}
                onTitleUpdated={(newTitle) =>
                  setAssessment((prev) =>
                    prev ? { ...prev, title: newTitle } : prev
                  )
                }
              />
            </div>
            <SubHeading>
              {assessment.total_candidates || 0} candidate
              {assessment.total_candidates === 1 ? "" : "s"} ·{" "}
              {assessment.completed_candidates || 0} completed
            </SubHeading>
            <StatusBadge status={assessment.status} />
          </div>
          <div className="flex space-x-2">
            {assessment.status === "draft" ? (
              <Button
                onClick={handlePublishAssessment}
                isLoading={isPublishing}
                variant="default"
                icon={<PlayIcon className="h-4 w-4" />}
              >
                Publish
              </Button>
            ) : assessment.status === "paused" ? (
              <>
                <Button
                  onClick={handlePauseToggle}
                  isLoading={isPausing}
                  variant="outline"
                  icon={<PlayIcon className="h-4 w-4" />}
                >
                  Unpause
                </Button>
              </>
            ) : (
              <>
                <Button
                  onClick={handlePauseToggle}
                  isLoading={isPausing}
                  variant="outline"
                  icon={<PauseIcon className="h-4 w-4" />}
                >
                  Pause
                </Button>
                <InviteDialog
                  assessmentId={assessment.id}
                  onInvite={loadCandidates}
                />
              </>
            )}
          </div>
        </div>
      </PageHeading>

      <PageContent>
        {error && <ErrorDisplay message={error} variant="full" />}

        <Tabs
          defaultValue={activeTab}
          className="w-full"
          onValueChange={handleTabChange}
        >
          <TabsList>
            <TabsTrigger value="overview">Overview</TabsTrigger>
            <TabsTrigger value="questions">Questions</TabsTrigger>
            {(assessment.status === "published" ||
              assessment.status === "paused") && (
              <TabsTrigger value="candidates">Candidates</TabsTrigger>
            )}
            <TabsTrigger value="settings">Settings</TabsTrigger>
          </TabsList>

          <TabsContent value="overview">
            <div className="bg-white rounded-lg p-6">Overview content</div>
          </TabsContent>

          <TabsContent value="questions">
            <div className="bg-white rounded-lg p-6">
              <div className="flex justify-between items-center mb-6">
                <h2 className="text-xl font-semibold">Assessment questions</h2>
                <EditQuestionsDialog
                  assessmentId={assessment.id}
                  questions={questions}
                  disabled={
                    assessment.status === "published" ||
                    assessment.status === "paused"
                  }
                  onQuestionsUpdated={() => {
                    // Refresh assessment data
                    if (!uuid) return;
                    apiRequest<Assessment>(
                      `${API_ENDPOINTS.assessments.list}/${uuid}`
                    ).then((data) => setAssessment(data));
                  }}
                />
              </div>

              <div className="space-y-6">
                {questions.map((question, index) => (
                  <div key={question.id} className="border rounded-lg p-4">
                    <div className="flex gap-4 items-center">
                      <span className="text-sm text-gray-500">{index + 1}</span>
                      <div className="flex-1 items-center flex">
                        <h3 className="font-medium">{question.text}</h3>

                        {question.type === "multiple-choice" &&
                          question.options && (
                            <div className="mt-4 space-y-2">
                              {question.options.map((option, optionIndex) => (
                                <div
                                  key={optionIndex}
                                  className="flex items-center gap-2 text-sm text-gray-700"
                                >
                                  <span className="w-5 h-5 flex items-center justify-center border rounded">
                                    {String.fromCharCode(65 + optionIndex)}
                                  </span>
                                  {option}
                                </div>
                              ))}
                            </div>
                          )}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </TabsContent>

          {assessment.status === "published" && (
            <TabsContent value="candidates">
              <div className="bg-white rounded-lg p-6">
                <div className="flex justify-between items-center mb-6">
                  <h2 className="text-xl font-semibold">
                    Assessment candidates
                  </h2>
                  <div className="flex space-x-2">
                    {selectedCandidates.length > 0 && (
                      <Button
                        variant="destructive"
                        size="sm"
                        icon={<TrashIcon className="h-4 w-4" />}
                        onClick={() => setShowRemoveCandidatesDialog(true)}
                      >
                        Remove selected ({selectedCandidates.length})
                      </Button>
                    )}
                  </div>
                </div>

                {isCandidatesLoading ? (
                  <div className="flex justify-center py-8">
                    <Loader title="Loading candidates" />
                  </div>
                ) : candidates.length === 0 ? (
                  <div className="text-center py-8 text-muted-foreground">
                    No candidates yet. Invite candidates to take the assessment.
                  </div>
                ) : (
                  <div className="relative overflow-x-auto">
                    <table className="w-full text-sm text-left">
                      <thead className="text-xs uppercase bg-muted">
                        <tr>
                          <th scope="col" className="px-2 py-3">
                            <Checkbox
                              checked={
                                selectedCandidates.length === candidates.length
                              }
                              onCheckedChange={(checked) => {
                                if (checked) {
                                  setSelectedCandidates(
                                    candidates.map((c) => c.id)
                                  );
                                } else {
                                  setSelectedCandidates([]);
                                }
                              }}
                            />
                          </th>
                          <th scope="col" className="px-6 py-3">
                            Email
                          </th>
                          <th scope="col" className="px-6 py-3">
                            Status
                          </th>
                          <th scope="col" className="px-6 py-3">
                            Invited
                          </th>
                          <th scope="col" className="px-6 py-3">
                            Actions
                          </th>
                        </tr>
                      </thead>
                      <tbody>
                        {candidates.map((candidate) => (
                          <tr key={candidate.id} className="bg-white border-b">
                            <td className="px-2 py-4">
                              <Checkbox
                                checked={selectedCandidates.includes(
                                  candidate.id
                                )}
                                onCheckedChange={(checked) => {
                                  if (checked) {
                                    setSelectedCandidates((prev) => [
                                      ...prev,
                                      candidate.id,
                                    ]);
                                  } else {
                                    setSelectedCandidates((prev) =>
                                      prev.filter((id) => id !== candidate.id)
                                    );
                                  }
                                }}
                              />
                            </td>
                            <td className="px-6 py-4">{candidate.email}</td>
                            <td className="px-6 py-4">
                              <StatusBadge status={candidate.status} />
                            </td>
                            <td className="px-6 py-4 text-muted-foreground">
                              {new Date(
                                candidate.created_at
                              ).toLocaleDateString()}
                            </td>
                            <td className="px-6 py-4">
                              <div className="flex flex-col space-y-2">
                                {candidate.status === "completed" && (
                                  <Button
                                    variant="outline"
                                    size="sm"
                                    onClick={() => {
                                      window.location.href = `/assessment/candidates/${candidate.id}?email=${candidate.email}`;
                                    }}
                                  >
                                    View candidate responses
                                  </Button>
                                )}

                                <ShareDialog
                                  assessmentId={candidate.id}
                                  assessmentLink={`${
                                    window.location.origin
                                  }/assessment/take/${
                                    candidate.id
                                  }?email=${encodeURIComponent(
                                    candidate.email
                                  )}`}
                                  compact={true}
                                  directCopy={true}
                                  onShare={() => {
                                    // Optional callback if needed
                                  }}
                                />
                              </div>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            </TabsContent>
          )}

          <TabsContent value="settings">
            <div className="bg-white rounded-lg p-6">Settings content</div>
          </TabsContent>
        </Tabs>
      </PageContent>

      {/* Remove candidates dialog */}
      <Dialog
        open={showRemoveCandidatesDialog}
        onOpenChange={setShowRemoveCandidatesDialog}
      >
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Remove selected candidates</DialogTitle>
            <DialogDescription>
              Are you sure you want to remove {selectedCandidates.length}{" "}
              candidate{selectedCandidates.length === 1 ? "" : "s"}? This action
              cannot be undone.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setShowRemoveCandidatesDialog(false)}
            >
              Cancel
            </Button>
            <Button
              variant="destructive"
              isLoading={isDeleting}
              onClick={async () => {
                setIsDeleting(true);
                try {
                  await apiRequest(API_ENDPOINTS.assessments.deleteCandidates, {
                    method: "DELETE",
                    body: { candidateIds: selectedCandidates },
                  });

                  // Refresh candidates list
                  await loadCandidates();

                  // Refresh assessment to update counts
                  if (uuid) {
                    const updatedAssessment = await apiRequest<Assessment>(
                      `${API_ENDPOINTS.assessments.list}/${uuid}`
                    );
                    setAssessment(updatedAssessment);
                  }

                  setSelectedCandidates([]);
                  setShowRemoveCandidatesDialog(false);

                  toast({
                    title: "Candidates removed",
                    description: `Successfully removed ${
                      selectedCandidates.length
                    } candidate${selectedCandidates.length === 1 ? "" : "s"}.`,
                    variant: "default",
                  });
                } catch (err: any) {
                  toast({
                    title: "Error",
                    description: err.message || "Failed to removed candidates",
                    variant: "destructive",
                  });
                } finally {
                  setIsDeleting(false);
                }
              }}
            >
              Remove
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
